import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import IconButton from "@mui/material/IconButton";
import { FaTimes } from "react-icons/fa";
import axios from "axios";
import BtnLoader from "Components/Common/Loader/BtnLoader";
import Swal from "sweetalert2";
import { loadDecrypted } from "Common Usage/localStorageUtils";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: 500,
  bgcolor: "background.paper",
  borderRadius: 8,
  boxShadow: 24,
  p: 4,
  maxHeight: "90vh",
  overflowY: "auto",
};

const TYPE_OPTIONS = [
  { label: "All Users", value: "all-users" },
  { label: "Male Users", value: "male-users" },
  { label: "Female Users", value: "female-users" },
  { label: "Gang Leaders", value: "gang-leaders" },
  { label: "Selected Users", value: "selected-users" },
];

const CreateBulkNotificationModal = ({ open, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: "",
    message: "",
    type: "all-users",
  });

  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [receivers, setReceivers] = useState([]);

  const [showUserSelector, setShowUserSelector] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  /* -------------------- Search Users (Debounced) -------------------- */
  useEffect(() => {
    const handler = setTimeout(async () => {
      if (!searchQuery.trim()) {
        setSuggestions([]);
        return;
      }

      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/users/get-name-number`,
          { params: { search: searchQuery } }
        );
        setSuggestions(res.data.data || []);
      } catch (err) {
        console.error("Error fetching users:", err);
      }
    }, 500);

    return () => clearTimeout(handler);
  }, [searchQuery]);

  /* -------------------- Handlers -------------------- */
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "type") {
      setShowUserSelector(value === "selected-users");
      setSelectedUsers([]);
      setReceivers([]);
      setSearchQuery("");
      setSuggestions([]);
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectUser = (user) => {
    if (selectedUsers.some((u) => u._id === user._id)) return;

    const updated = [...selectedUsers, user];
    setSelectedUsers(updated);
    setReceivers(updated.map((u) => u._id));

    setSearchQuery("");
    setSuggestions([]);
  };

  const handleRemoveUser = (id) => {
    const updated = selectedUsers.filter((u) => u._id !== id);
    setSelectedUsers(updated);
    setReceivers(updated.map((u) => u._id));
  };

  /* -------------------- Submit -------------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim() || !formData.message.trim()) {
      setError("Title and message are required");
      return;
    }

    if (formData.type === "selected-users" && receivers.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "No Users Selected",
        text: "Please select at least one user",
      });
      return;
    }

    setIsLoading(true);
    setError("");

    const senderId = loadDecrypted("HeartConnect-StaffId");

    try {
      const payload = {
        senderId,
        title: formData.title,
        message: formData.message,
      };

      if (formData.type === "selected-users") {
        payload.receiverId = receivers;
      } else {
        payload.type = formData.type;
      }

      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/notification/send-bulk-notification`,
        payload
      );

      if (res.data.success) {
        Swal.fire({
          icon: "success",
          title: "Notification Sent",
          text: res.data.message,
          timer: 2000,
          showConfirmButton: true,
        });

        setFormData({ title: "", message: "", type: "all-users" });
        setSelectedUsers([]);
        setReceivers([]);
        setShowUserSelector(false);
        onClose();
        onSuccess?.();
      } else {
        setError(res.data.message || "Failed to send notification");
      }
    } catch (err) {
      console.error(err);
      setError("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  /* -------------------- UI -------------------- */
  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={style}>
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8, color: "#c51369" }}
        >
          <FaTimes />
        </IconButton>

        <form onSubmit={handleSubmit} className="space-y-6">
          <h2 className="text-center text-2xl font-bold text-navy-700">
            Create Notification
          </h2>

          {/* Notification Type */}
          <div>
            <label className="mb-1 block text-lg">Notification Type</label>
            <select
              name="type"
              value={formData.type}
              onChange={handleChange}
              className="w-full rounded-md border p-2"
            >
              {TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* User Selector */}
          {showUserSelector && (
            <div>
              <label className="text-md font-medium">Users</label>

              {/* Selected Chips */}
              {selectedUsers.length > 0 && (
                <div className="mb-2 flex flex-wrap gap-2">
                  {selectedUsers.slice(0, 3).map((u) => (
                    <div
                      key={u._id}
                      className="flex items-center gap-2 rounded bg-gray-100 px-3 py-1 text-sm"
                    >
                      <span>{u.fullName}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveUser(u._id)}
                        className="text-gray-600 hover:text-red-500"
                      >
                        <FaTimes size={12} />
                      </button>
                    </div>
                  ))}

                  {selectedUsers.length > 3 && (
                    <div className="rounded bg-gray-200 px-3 py-1 text-sm text-gray-700">
                      +{selectedUsers.length - 3} more
                    </div>
                  )}
                </div>
              )}

              {/* Search Input */}
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Full Name / Nickname / Phone"
                  className="w-full rounded border px-3 py-2"
                />

                {suggestions.length > 0 && (
                  <ul className="absolute left-0 top-full z-20 mt-1 max-h-44 w-full overflow-auto rounded border bg-white shadow">
                    {suggestions.map((u) => (
                      <li
                        key={u._id}
                        onClick={() => handleSelectUser(u)}
                        className="cursor-pointer px-3 py-2 hover:bg-gray-100"
                      >
                        <div className="text-sm font-medium">{u.fullName}</div>
                        <div className="text-xs text-gray-500">{u.phone}</div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          {/* Title */}
          <div>
            <label className="mb-1 block text-lg">Title</label>
            <input
              name="title"
              value={formData.title}
              onChange={handleChange}
              className="w-full rounded-md border p-2"
            />
          </div>

          {/* Message */}
          <div>
            <label className="mb-1 block text-lg">Message</label>
            <textarea
              name="message"
              rows={5}
              value={formData.message}
              onChange={handleChange}
              className="w-full rounded-md border p-2"
            />
          </div>

          {error && <p className="text-center text-red-500">{error}</p>}

          <button
            type="submit"
            className="flex w-full justify-center rounded-md bg-[#c51369] p-3 text-white hover:bg-[#a10f5b]"
            disabled={isLoading}
          >
            {isLoading ? <BtnLoader /> : "Send Notification"}
          </button>
        </form>
      </Box>
    </Modal>
  );
};

export default CreateBulkNotificationModal;
